import classNames from 'classnames';
import { Fragment } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import {
  So5LeaderboardSeasonality,
  type So5FixtureEvent,
} from '@sorare/core/src/__generated__/globalTypes';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip';
import { LabelS } from '@sorare/core/src/atoms/typography';
import { SportSeasonalityIcon } from '@sorare/core/src/components/SportSeasonalityIcon';
import type { PrintablePlayerSortName } from '@sorare/core/src/contexts/config';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useScreenSize } from '@sorare/core/src/hooks/device/useScreenSize';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import {
  cardAttributes,
  filters,
  glossary,
  navLabels,
} from '@sorare/core/src/lib/glossary';
import {
  PlayerSearchViewAttributes,
  PriceDisplay,
  StatsOnboarding,
} from '@sorare/core/src/lib/playerSearchView';
import {
  averageScoreSorts,
  breakdownScoreSorts,
  projectionDifferenceSorts,
  projectionGradeSorts,
  projectionSorts,
} from '@sorare/core/src/lib/scouting-center';
import { laptopAndAbove } from '@sorare/core/src/style/mediaQuery';
import { css as cssClass } from '@sorare/css';

import { AveragePlayerScoreLabel } from '#src/components/searchPlayerTable/AveragePlayerScoreLabel/index.tsx';
import type { Breakdown } from '#src/components/searchPlayerTable/BreakdownScoreLabel/index.tsx';
import { BreakdownScoreLabel } from '#src/components/searchPlayerTable/BreakdownScoreLabel/index.tsx';
import { priceColumn } from '#src/components/searchPlayerTable/PriceCell/index.tsx';
import { ProjectionDifferenceLabel } from '#src/components/searchPlayerTable/ProjectionDifferenceLabel/index.tsx';
import type { ProjectionGrade } from '#src/components/searchPlayerTable/ProjectionGradeLabel/index.tsx';
import { ProjectionGradeLabel } from '#src/components/searchPlayerTable/ProjectionGradeLabel/index.tsx';
import type { Projection } from '#src/components/searchPlayerTable/ProjectionLabel/index.tsx';
import { ProjectionLabel } from '#src/components/searchPlayerTable/ProjectionLabel/index.tsx';
import { SearchTableHeader } from '#src/components/searchPlayerTable/SearchTableHeader/index.tsx';
import {
  MatchupsCell as MatchupsCellGeneric,
  StatsCell,
} from '#src/components/searchPlayerTable/SearchTableHeader/ui.tsx';
import {
  SortByPrice,
  messages as marketValueMessages,
} from '#src/components/searchPlayerTable/SortByPrice/index.tsx';
import { SortTable } from '#src/components/searchPlayerTable/SortTable/index.tsx';
import { PROJECTION_WITH_DIFFERENCE_BY_SPORT } from '#src/lib/projection.ts';
import { useManagePlayerSearchView } from '#src/searchCommonPlayer/StatsManager/useManagePlayerSearchView.ts';

import { StatCellLabel } from './StatCellLabel/index.tsx';

const PlayerName = styled.div`
  width: var(--player-metas-width);
  flex-shrink: 0;
`;

const StatsIcon = styled.div`
  width: var(--player-stats-icon-width);
`;

const priceGroupCell = cssClass`
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: var(--unit);
  flex: 1;
  min-width: 0;
  background-color: var(--c-nd-50-opaque);
  padding-block: var(--half-unit);
`;

const priceTitle = cssClass`
  white-space: normal;
  text-align: center;
`;

const MatchupsCell = styled(MatchupsCellGeneric)`
  display: none;
  @media ${laptopAndAbove} {
    display: flex;
  }
`;

const getStatsOnboarding = (index: number) => {
  switch (index) {
    case 0:
      return StatsOnboarding.SORT_BY_CLICKING;
    case 1:
      return StatsOnboarding.COMBINE_SORTS;
    default:
      return undefined;
  }
};

type Props = {
  sorts: PrintablePlayerSortName[];
  refine: (_sorts: PrintablePlayerSortName[]) => void;
};

export const SearchPlayerTableHeader = ({ sorts, refine }: Props) => {
  const { sport } = useSportContext();
  const {
    flags: { useHandleL10Gain = false },
  } = useFeatureFlags();
  const withDifference =
    useHandleL10Gain && PROJECTION_WITH_DIFFERENCE_BY_SPORT[sport];
  const { statsManager, displayableStats, statsSorts } =
    useManagePlayerSearchView();
  const { up: laptopOrAbove } = useScreenSize('laptop');

  const isFloorPriceDisplay =
    statsManager[PlayerSearchViewAttributes.PRICE_DISPLAY] ===
    PriceDisplay.FLOOR_PRICE;
  const priceColumnTitle = (
    <Horizontal gap={0.5}>
      <SportSeasonalityIcon
        seasonality={statsManager[PlayerSearchViewAttributes.SEASONALITY]}
      />
      <LabelS
        color="var(--c-nd-400)"
        className={classNames('uppercase', priceTitle)}
      >
        <FormattedMessage
          {...(isFloorPriceDisplay
            ? glossary.lowestPrice
            : filters.marketValues)}
        />
      </LabelS>
    </Horizontal>
  );

  return (
    <SearchTableHeader
      firstCell={
        <Horizontal gap={2}>
          <PlayerName>
            <SortTable
              indexes={{
                asc: 'ASC-name_for_sorting',
                desc: 'DESC-name_for_sorting',
              }}
              ascFirst
              sorts={sorts}
              refine={refine}
            >
              <LabelS className="uppercase">
                <FormattedMessage {...cardAttributes.player} />
              </LabelS>
            </SortTable>
          </PlayerName>
          {!!laptopOrAbove && <StatsIcon />}
        </Horizontal>
      }
      lastCell={
        <div className={priceGroupCell}>
          <Horizontal gap={0.5} center>
            {isFloorPriceDisplay ? (
              priceColumnTitle
            ) : (
              <Tooltip
                title={
                  <FormattedMessage
                    {...(statsManager[
                      PlayerSearchViewAttributes.SEASONALITY
                    ] === So5LeaderboardSeasonality.IN_SEASON
                      ? marketValueMessages.inSeasonTooltip
                      : marketValueMessages.classicTooltip)}
                  />
                }
              >
                {priceColumnTitle}
              </Tooltip>
            )}
          </Horizontal>
          <Horizontal gap={0.5}>
            {statsManager[PlayerSearchViewAttributes.MARKET_VALUE_RARITIES].map(
              rarity => (
                <div key={rarity} className={priceColumn}>
                  <SortByPrice rarity={rarity} sorts={sorts} refine={refine} />
                </div>
              )
            )}
          </Horizontal>
        </div>
      }
    >
      <MatchupsCell>
        <LabelS color="var(--c-nd-400)" className="uppercase">
          <FormattedMessage {...navLabels.matchups} />
        </LabelS>
      </MatchupsCell>
      {statsManager[PlayerSearchViewAttributes.PROJECTION_GRADE].map(
        (so5FixtureEvent: So5FixtureEvent) =>
          so5FixtureEvent in projectionGradeSorts && (
            <StatsCell key={so5FixtureEvent}>
              <SortTable
                indexes={
                  projectionGradeSorts[
                    so5FixtureEvent as keyof typeof projectionGradeSorts
                  ]
                }
                sorts={sorts}
                refine={refine}
              >
                <ProjectionGradeLabel
                  mode={so5FixtureEvent as ProjectionGrade}
                />
              </SortTable>
            </StatsCell>
          )
      )}
      {statsManager[PlayerSearchViewAttributes.AVERAGE_PLAYER_SCORE].map(
        (averageScore, i) => (
          <StatsCell key={averageScore}>
            <SortTable
              onboarding={getStatsOnboarding(i)}
              indexes={averageScoreSorts[averageScore]}
              sorts={sorts}
              refine={refine}
            >
              <AveragePlayerScoreLabel mode={averageScore} />
            </SortTable>
          </StatsCell>
        )
      )}
      {statsManager[PlayerSearchViewAttributes.PROJECTION].map(
        (so5FixtureEvent: So5FixtureEvent) =>
          so5FixtureEvent in projectionSorts && (
            <Fragment key={so5FixtureEvent}>
              <StatsCell key={so5FixtureEvent}>
                <SortTable
                  indexes={
                    projectionSorts[
                      so5FixtureEvent as keyof typeof projectionSorts
                    ]
                  }
                  sorts={sorts}
                  refine={refine}
                >
                  <ProjectionLabel mode={so5FixtureEvent as Projection} />
                </SortTable>
              </StatsCell>
              {!!withDifference && (
                <StatsCell key={so5FixtureEvent}>
                  <SortTable
                    indexes={
                      projectionDifferenceSorts[
                        so5FixtureEvent as keyof typeof projectionDifferenceSorts
                      ]
                    }
                    sorts={sorts}
                    refine={refine}
                  >
                    <ProjectionDifferenceLabel
                      mode={so5FixtureEvent as Projection}
                    />
                  </SortTable>
                </StatsCell>
              )}
            </Fragment>
          )
      )}

      {statsManager[PlayerSearchViewAttributes.BREAKDOWN].map(
        breakdown =>
          breakdown in breakdownScoreSorts && (
            <StatsCell key={breakdown}>
              <SortTable
                indexes={
                  breakdownScoreSorts[
                    breakdown as keyof typeof breakdownScoreSorts
                  ]
                }
                sorts={sorts}
                refine={refine}
              >
                <BreakdownScoreLabel mode={breakdown as Breakdown} />
              </SortTable>
            </StatsCell>
          )
      )}
      {displayableStats.map(stat => {
        return (
          <StatsCell key={stat}>
            <SortTable
              indexes={statsSorts[stat as keyof typeof statsSorts]}
              sorts={sorts}
              refine={refine}
            >
              <StatCellLabel stat={stat} />
            </SortTable>
          </StatsCell>
        );
      })}
    </SearchTableHeader>
  );
};
